/* eslint-disable react/jsx-no-bind */
import { Button, Drawer, Table, TablePaginationConfig } from 'antd'
import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getAdminsAction } from 'slices/admins'
import { AdminUser } from 'types'
import EditAdminForm from 'container/EditAdminForm'
import { EditOutlined } from '@ant-design/icons'

/**
 * AdminsTable
 * component props
 */

interface PropTypes {
	query: string
}

/**
 * Component for displaying admins table in AdminsPageLayout
 *
 * @param query - search query for admins
 * @returns Component AdminsTable
 * with pagination
 */

const AdminsTable: React.FC<PropTypes> = ({ query }) => {
	const dispatch = useAppDispatch()
	const { records, currentPage, totalCount, loading } = useAppSelector(
		state => state.admin
	)
	const [drawer, setDrawer] = useState(false)

	const [singleAdmin, setSingleAdmin] = useState<AdminUser | null>(null)

	function openDrawer(data: AdminUser) {
		setSingleAdmin(data)
		setDrawer(true)
	}
	function closeDrawer() {
		setDrawer(false)
	}

	/**
	 * Hardcoded size of the table
	 */
	const size = 10

	/**
	 * Pagination config for the table
	 */
	const pagination = {
		current: currentPage,
		total: totalCount,
		pageSize: size,
	}

	/**
	 * Fetch admins on mount and on query change
	 */

	useEffect(() => {
		const options = {
			page: 1,
			size: pagination.pageSize,
			query,
		}
		dispatch(getAdminsAction(options))
	}, [query])

	/**
	 * Columns for the table of admins
	 */
	const columns = [
		{
			title: 'Name',
			dataIndex: 'firstName',
			render: (firstName: string, data: AdminUser) =>
				`${firstName} ${data.lastName && data.lastName}`,
		},
		{
			title: 'Mobile',
			dataIndex: 'mobile',
		},
		{
			title: 'Email',
			dataIndex: 'email',
		},
		{
			title: 'Active',
			dataIndex: 'active',
			render: (active: boolean) => `${active ? 'Active' : 'Inactive'}`,
		},
		{
			title: 'Action',
			key: 'action',
			dataIndex: '_id',
			render: (id: string, data: AdminUser) => (
				<Button
					type='primary'
					icon={<EditOutlined />}
					onClick={() => openDrawer(data)}
				/>
			),
		},
	]

	/**
	 * Handles pagination change by changing the current page in the store.
	 *
	 * @param page - Pagination object from the table
	 */

	const handlePageChange = (page: TablePaginationConfig) => {
		const options = {
			page: page.current || 1,
			size: page.pageSize || size,
			query,
		}
		dispatch(getAdminsAction(options))
	}

	return (
		<>
			<Table
				columns={columns}
				rowKey={record => record._id}
				dataSource={records}
				pagination={{ ...pagination, position: ['bottomCenter'] }}
				loading={loading}
				onChange={handlePageChange}
			/>
			<Drawer
				title='Add Admin'
				placement='right'
				width='30vw'
				closable
				visible={drawer}
				onClose={closeDrawer}
				destroyOnClose
			>
				{singleAdmin !== null && (
					<EditAdminForm closeDrawer={closeDrawer} singleAdmin={singleAdmin} />
				)}
			</Drawer>
		</>
	)
}

export default AdminsTable
